// Klientský výpočet dynamických podskupin (elevation_interval/elevation_cluster/spatial_cluster).
// Port serverové logiky z cw-server/src/routes/station_group.js — přesunuto na klienta, protože
// clustering pro vnořené úrovně musí běžet zvlášť pro každý uzel předchozí úrovně (viz buildGroupTree),
// s čímž server (jedna globální sada stanic datasource) nepočítá.

const KMEANS_MAX_ITER = 25

function squaredDist(a, b) {
  const dLat = a.lat - b.lat
  const dLon = a.lon - b.lon
  return dLat * dLat + dLon * dLon
}

// Seedovaný PRNG (mulberry32) — nahrazuje Math.random(), aby stejná vstupní množina stanic dala
// při každém přepočtu STEJNÉ clustery (viz computeDynamicGroups: seed se odvozuje ze setříděných
// gh_id). Bez toho by k-means++ inicializace byla pokaždé jiná a uložená skupina by se vizuálně
// „přeskupovala" při každém otevření, i když se nic nezměnilo.
function mulberry32(seed) {
  let a = seed
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// 32bitový string hash (FNV-1a) — seed pro mulberry32 odvozený z obsahu vstupu.
function hashString(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

function kMeansPlusPlusInit(points, k, rng) {
  const centroids = [points[Math.floor(rng() * points.length)]]
  while (centroids.length < k) {
    const distances = points.map(p => Math.min(...centroids.map(c => squaredDist(p, c))))
    const sum = distances.reduce((a, b) => a + b, 0)
    if (sum === 0) {
      centroids.push(points[Math.floor(rng() * points.length)])
      continue
    }
    let r = rng() * sum
    let idx = 0
    for (; idx < points.length; idx++) {
      r -= distances[idx]
      if (r <= 0) break
    }
    centroids.push(points[Math.min(idx, points.length - 1)])
  }
  return centroids.map(c => ({ lat: c.lat, lon: c.lon }))
}

// Kapacitně omezený k-means: na rozdíl od čistého k-means++ respektuje limit velikosti clusteru
// (capacity = ⌈target⌉) už při PŘIŘAZOVÁNÍ bodů, ne až dodatečným přesunem po konvergenci — díky
// tomu nevznikají osamocené "ostrůvky" (post-hoc vyvažování je vždy jen kompromis, protože musí
// vytrhávat body z jejich přirozeně nejbližšího clusteru). Přiřazení probíhá greedy podle vzdálenosti:
// ze všech dvojic (bod, centroid) se opakovaně vybere nejbližší pár, kde má centroid ještě volnou
// kapacitu; centroidy se pak přepočítají a přiřazení se zopakuje do konvergence (Lloydův algoritmus
// s kapacitním omezením místo prostého "nejbližší centroid").
function kMeansPlusPlus(points, k, rng) {
  if (points.length === 0) return []
  if (k >= points.length) return points.map(p => [p])

  let centroids = kMeansPlusPlusInit(points, k, rng)
  const capacity = Math.ceil(points.length / k)
  let assignments = new Array(points.length).fill(-1)

  for (let iter = 0; iter < KMEANS_MAX_ITER; iter++) {
    const newAssignments = capacityConstrainedAssign(points, centroids, capacity)
    const changed = newAssignments.some((a, i) => a !== assignments[i])
    assignments = newAssignments
    if (!changed && iter > 0) break

    const sums = centroids.map(() => ({ lat: 0, lon: 0, n: 0 }))
    for (let i = 0; i < points.length; i++) {
      const s = sums[assignments[i]]
      s.lat += points[i].lat; s.lon += points[i].lon; s.n++
    }
    centroids = centroids.map((c, idx) => sums[idx].n > 0
      ? { lat: sums[idx].lat / sums[idx].n, lon: sums[idx].lon / sums[idx].n }
      : c)
  }

  // Greedy přiřazení podle globálního pořadí vzdáleností může nechat "přetéct" body do vzdálenějšího
  // clusteru, i když by záměna se sousedním clusterem oba zkompaktnila (viz prolnuté shluky na mapě) —
  // doladí to lokální swap-optimalizace: prohodí dvojice bodů z různých clusterů, kdykoli to sníží
  // součet vzdáleností k centroidům (kapacita zůstává nedotčená, protože jde vždy o výměnu 1:1).
  assignments = swapOptimize(points, centroids, assignments, k)

  const clusters = centroids.map(() => [])
  points.forEach((p, i) => clusters[assignments[i]].push(p))
  return clusters.filter(c => c.length > 0)
}

// O(n²) na pass — pro stovky bodů na úroveň hierarchie (typický vstup) v pořádku, ale nad tímto
// stropem by UI znatelně sekalo, takže se nad ním swap-optimalizace přeskočí (zůstane jen výsledek
// z capacityConstrainedAssign, o něco méně kompaktní, ale pořád bez ztráty dat).
const SWAP_OPTIMIZE_MAX_POINTS = 400

function swapOptimize(points, centroids, assignments, k) {
  if (points.length > SWAP_OPTIMIZE_MAX_POINTS) return assignments

  const result = [...assignments]
  const maxPasses = 20

  for (let pass = 0; pass < maxPasses; pass++) {
    let improved = false
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const ci = result[i], cj = result[j]
        if (ci === cj) continue
        const before = squaredDist(points[i], centroids[ci]) + squaredDist(points[j], centroids[cj])
        const after = squaredDist(points[i], centroids[cj]) + squaredDist(points[j], centroids[ci])
        if (after < before) {
          result[i] = cj
          result[j] = ci
          improved = true
        }
      }
    }
    if (!improved) break
  }

  return result
}

// Greedy přiřazení bodů centroidům s omezenou kapacitou: seřadí všechny dvojice (bod, centroid)
// podle vzdálenosti vzestupně a postupně přiřazuje — bod dostane nejbližší centroid, který má
// ještě volné místo. Rychlejší varianta „Auction“/„greedy transportation" algoritmu, dost dobrá
// pro stovky bodů na úroveň hierarchie.
function capacityConstrainedAssign(points, centroids, capacity) {
  const pairs = []
  for (let p = 0; p < points.length; p++) {
    for (let c = 0; c < centroids.length; c++) {
      pairs.push({ p, c, d: squaredDist(points[p], centroids[c]) })
    }
  }
  pairs.sort((a, b) => a.d - b.d)

  const assignments = new Array(points.length).fill(-1)
  const counts = new Array(centroids.length).fill(0)
  let remaining = points.length

  for (const { p, c, d } of pairs) {
    if (remaining === 0) break
    if (assignments[p] !== -1) continue
    if (counts[c] >= capacity) continue
    assignments[p] = c
    counts[c]++
    remaining--
  }
  return assignments
}

// stations = pole { gh_id, elevation, latitude, longitude } (subset allStations omezený na množinu rodiče/filtrů)
// defType = 'elevation_interval' | 'elevation_cluster' | 'spatial_cluster', n = level.param
// Vrací [{ code, label, station_count, ghIds: [gh_id,...] }]
export function computeDynamicGroups(stations, defType, n) {
  const usable = stations.filter(s => s.elevation != null && s.latitude != null && s.longitude != null)

  let groups
  if (defType === 'elevation_interval') {
    const buckets = new Map()
    for (const s of usable) {
      const from = Math.floor(s.elevation / n) * n
      if (!buckets.has(from)) buckets.set(from, [])
      buckets.get(from).push(s)
    }
    groups = [...buckets.entries()]
      .sort(([a], [b]) => a - b)
      .map(([from, members]) => ({
        code: `${from}-${from + n}`,
        label: `${from}–${from + n} m n.m.`,
        members,
      }))
  } else if (defType === 'elevation_cluster') {
    const sorted = [...usable].sort((a, b) => a.elevation - b.elevation)
    groups = []
    for (let i = 0; i < sorted.length; i += n) {
      const members = sorted.slice(i, i + n)
      const from = members[0].elevation
      const to = members[members.length - 1].elevation
      groups.push({
        code: `cluster-${groups.length + 1}`,
        label: `${Math.round(from)}–${Math.round(to)} m n.m.`,
        members,
      })
    }
  } else if (defType === 'spatial_cluster') {
    const k = Math.ceil(usable.length / n)
    // Seřazeno podle gh_id, aby výsledek nezávisel na pořadí vstupního pole stations (jen na jeho
    // OBSAHU) — kMeansPlusPlusInit vybírá počáteční centroidy podle indexu v poli, takže bez tohoto
    // seřazení by stejná množina stanic v jiném pořadí (např. po jiném řazení z API) dala jiné clustery.
    const points = [...usable].sort((a, b) => a.gh_id.localeCompare(b.gh_id)).map(s => ({ ...s, lat: s.latitude, lon: s.longitude }))
    // Seed odvozený ze vstupu (setříděné gh_id + parametr n) — stejná množina stanic a stejné n
    // dají vždy stejné centroidy/clustery, i když se strom přepočítá znovu (jiný filtr, re-render, ...).
    const seed = hashString(points.map(s => s.gh_id).join(',') + '|' + n)
    const rng = mulberry32(seed)
    const clusters = k > 0 ? kMeansPlusPlus(points, k, rng) : []
    groups = clusters.map((members, idx) => ({
      code: `spatial-${idx + 1}`,
      label: `Prostorový shluk ${idx + 1}`,
      members,
    }))
  } else {
    groups = []
  }

  return groups.map(g => ({
    code: g.code,
    label: g.label,
    station_count: g.members.length,
    ghIds: g.members.map(s => s.gh_id).sort(),
  }))
}

// --- Geometrické dělení "podle počtu" (splitByCount) -------------------------------------
// Rozdělí množinu stanic na 2 / 4 / 5 / 9 geograficky vymezených dílů (světové strany +
// volitelně střed), nebo s mode='auto' zvolí počet dílů podle počtu stanic a perGroup.
// Na rozdíl od spatial_cluster (k-means) je dělení čistě geometrické (mediány / bbox / úhel
// od těžiště), takže je deterministické bez seedovaného PRNG.

// Kód směru → label podle jazyka (viz doc/plans/group-context-merge-split.md 4.7)
export const DIRECTION_LABELS = {
  cs: { N: 'S', S: 'J', E: 'V', W: 'Z', NE: 'SV', SE: 'JV', SW: 'JZ', NW: 'SZ', C: 'střed' },
  en: { N: 'N', S: 'S', E: 'E', W: 'W', NE: 'NE', SE: 'SE', SW: 'SW', NW: 'NW', C: 'center' },
}

// Pevné pořadí kódů na výstupu pro daný mód (stabilní pořadí uzlů ve stromu / barev v legendě)
const DIR_ORDER = {
  2: ['N', 'S', 'E', 'W'],
  4: ['NE', 'SE', 'SW', 'NW'],
  5: ['NE', 'SE', 'SW', 'NW', 'C'],
  9: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'C'],
}

const DEG2RAD = Math.PI / 180

function median(sortedNums) {
  const n = sortedNums.length
  if (n === 0) return 0
  const mid = n >> 1
  return n % 2 ? sortedNums[mid] : (sortedNums[mid - 1] + sortedNums[mid]) / 2
}

// Rozhodne mode pro 'auto' z počtu stanic n a cílového počtu na díl p (viz 4.2)
function autoMode(n, p) {
  if (n <= p) return 1
  if (n <= 2 * p) return 2
  if (n <= 4 * p) return 4
  if (n <= 5 * p) return 5
  return 9
}

// stations = [{ gh_id, latitude, longitude }, ...]
// mode = 'auto' | 2 | 4 | 5 | 9
// perGroup = cílový počet stanic na díl (jen pro 'auto', default 10)
// Vrací [{ dir, ghIds: [gh_id,...] }] — dir = neutrální kód ('N'/'NE'/.../'C'), v pořadí DIR_ORDER,
// prázdné díly vynechány. Stanice bez lat/lon zůstávají nezařazené (nejsou v žádném dílu).
export function splitByCount(stations, mode = 'auto', perGroup = 10) {
  const usable = stations.filter(s => s.latitude != null && s.longitude != null)
  if (usable.length === 0) return []

  const midLat = usable.reduce((a, s) => a + s.latitude, 0) / usable.length
  const cosLat = Math.cos(midLat * DEG2RAD) || 1e-6
  // pracovní body: x = lon * cos(midLat) kvůli korekci délky pro ČR
  const pts = usable.map(s => ({ gh_id: s.gh_id, lat: s.latitude, x: s.longitude * cosLat }))

  const m = mode === 'auto' ? autoMode(pts.length, perGroup > 0 ? perGroup : 10) : Number(mode)

  if (m === 1) return [{ dir: null, ghIds: pts.map(p => p.gh_id).sort() }]

  const collect = (assign) => {
    const byDir = new Map()
    for (const p of pts) {
      const d = assign(p)
      if (!byDir.has(d)) byDir.set(d, [])
      byDir.get(d).push(p.gh_id)
    }
    return DIR_ORDER[m]
      .filter(d => byDir.has(d))
      .map(d => ({ dir: d, ghIds: byDir.get(d).sort() }))
  }

  if (m === 2) {
    const lats = pts.map(p => p.lat).sort((a, b) => a - b)
    const xs = pts.map(p => p.x).sort((a, b) => a - b)
    const latSpan = lats[lats.length - 1] - lats[0]
    const xSpan = xs[xs.length - 1] - xs[0]
    if (latSpan >= xSpan) {
      const mLat = median(lats)
      return collect(p => (p.lat >= mLat ? 'N' : 'S'))
    }
    const mX = median(xs)
    return collect(p => (p.x >= mX ? 'E' : 'W'))
  }

  if (m === 4) {
    const mLat = median(pts.map(p => p.lat).sort((a, b) => a - b))
    const mX = median(pts.map(p => p.x).sort((a, b) => a - b))
    return collect(p => (p.lat >= mLat ? 'N' : 'S') + (p.x >= mX ? 'E' : 'W'))
  }

  // m === 5 nebo 9: nejdřív vyčlenit střed C = round(n / dílů) nejbližších těžišti,
  // pak zbytek rozdělit (kvadranty dle mediánů zbytku pro 5, úhlové sektory pro 9).
  const meanLat = pts.reduce((a, p) => a + p.lat, 0) / pts.length
  const meanX = pts.reduce((a, p) => a + p.x, 0) / pts.length
  const d2 = (p) => (p.lat - meanLat) ** 2 + (p.x - meanX) ** 2
  const k = Math.max(0, Math.round(pts.length / m))
  const byCenterDist = [...pts].sort((a, b) => d2(a) - d2(b) || a.gh_id.localeCompare(b.gh_id))
  const centerSet = new Set(byCenterDist.slice(0, k).map(p => p.gh_id))
  const rest = pts.filter(p => !centerSet.has(p.gh_id))

  const byDir = new Map([['C', [...centerSet]]])
  if (m === 5) {
    const mLat = median(rest.map(p => p.lat).sort((a, b) => a - b))
    const mX = median(rest.map(p => p.x).sort((a, b) => a - b))
    for (const p of rest) {
      const d = (p.lat >= mLat ? 'N' : 'S') + (p.x >= mX ? 'E' : 'W')
      if (!byDir.has(d)) byDir.set(d, [])
      byDir.get(d).push(p.gh_id)
    }
  } else {
    // 8 sektorů po 45°, střed sektoru: E=0°, N=90°, W=±180°, S=-90°
    const SECTORS = [
      { dir: 'E', c: 0 }, { dir: 'NE', c: 45 }, { dir: 'N', c: 90 }, { dir: 'NW', c: 135 },
      { dir: 'W', c: 180 }, { dir: 'SW', c: -135 }, { dir: 'S', c: -90 }, { dir: 'SE', c: -45 },
    ]
    for (const p of rest) {
      const ang = Math.atan2(p.lat - meanLat, p.x - meanX) / DEG2RAD
      let best = SECTORS[0], bestDiff = 999
      for (const s of SECTORS) {
        let diff = Math.abs(((ang - s.c + 540) % 360) - 180)
        if (diff < bestDiff) { bestDiff = diff; best = s }
      }
      if (!byDir.has(best.dir)) byDir.set(best.dir, [])
      byDir.get(best.dir).push(p.gh_id)
    }
  }
  return DIR_ORDER[m]
    .filter(d => byDir.has(d) && byDir.get(d).length > 0)
    .map(d => ({ dir: d, ghIds: byDir.get(d).sort() }))
}
